import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileCheck,
  Loader2,
  Search,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { ONBOARDING_NUDGE_ACCOUNTANT_INVITE } from "@/hooks/useOnboardingPending";
import { cn } from "@/lib/utils";
import { dismissDashboardAlert } from "@/services/dashboardService";
import {
  updateTenant,
  validateRut,
  type CompanyTypeValue,
} from "@/services/authService";
import { translateAuthErrorMessage } from "@/utils/translateAuthErrorMessage";

const RUC_DIGITS = 12;

function normalizeRut(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, RUC_DIGITS);
}

export function AccountantNewClientPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createCompanyAndSetUser } = useAuth();

  const [rut, setRut] = useState("");
  const [legalName, setLegalName] = useState<string | null>(null);
  const [legalAddress, setLegalAddress] = useState("");
  const [city, setCity] = useState("");
  const [department, setDepartment] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [companyType, setCompanyType] = useState<CompanyTypeValue | "">("");
  // Nuevo-emisor gate. "already_issuer" keeps the existing DGI onboarding
  // (default — the common case). "scratch" routes the client into the
  // homologación-simplificada flow for brand-new electronic emisores.
  const [emisorChoice, setEmisorChoice] = useState<
    "scratch" | "already_issuer"
  >("already_issuer");
  // Two-step wizard: 1 = find the client (RUT + DGI data + contact),
  // 2 = pick nuevo/existente emisor. Kept as a distinct step so the choice
  // that routes the whole DGI onboarding isn't buried under the client form.
  const [step, setStep] = useState<1 | 2>(1);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const normalizedRut = normalizeRut(rut);
  const rutValid =
    normalizedRut.length === RUC_DIGITS && /^\d+$/.test(normalizedRut);
  const verified = Boolean(legalName);
  const canContinue =
    verified &&
    contactEmail.trim() !== "" &&
    phone.trim() !== "" &&
    companyType !== "";

  const handleValidateRut = async (): Promise<void> => {
    if (!rutValid) {
      toast.error(
        t(
          "accountantNewClient.rutFormatError",
          "El RUT debe tener 12 dígitos.",
        ),
      );
      return;
    }
    setValidating(true);
    try {
      const result = await validateRut(normalizedRut);
      setLegalName(result.legalName ?? normalizedRut);
      setCompanyType(result.suggestedCompanyType ?? "");
      setLegalAddress(result.legalAddress ?? "");
      setCity(result.city ?? "");
      setDepartment(result.department ?? "");
      setPhone(result.phone ?? "");
      setContactEmail(result.email ?? "");
      toast.success(
        t("accountantNewClient.rutValidated", "Empresa encontrada."),
      );
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      const data =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data
          : undefined;
      const msg = data?.message as string | undefined;
      const message =
        status === 409
          ? t(
              "accountantNewClient.rutAlreadyRegistered",
              "Ya existe una empresa con ese RUT en Fiscalo.",
            )
          : status === 503
            ? t(
                "accountantNewClient.rutUnavailable",
                "DGI no responde ahora mismo. Probá en un momento.",
              )
            : (translateAuthErrorMessage(msg, t) ??
              t(
                "accountantNewClient.rutInvalid",
                "No pudimos validar este RUT.",
              ));
      toast.error(message);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!canContinue || submitting) return;
    setSubmitting(true);
    try {
      const { tenantId } = await createCompanyAndSetUser(normalizedRut, {
        legalName: legalName ?? undefined,
        companyType: companyType || undefined,
        address: legalAddress.trim() || undefined,
        city: city.trim() || undefined,
        department: department.trim() || undefined,
        phone: phone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      });
      // The new tenant is now active (session.activeTenantId), so the
      // updateTenant call below (PATCH /auth/tenant) targets it. Persist the
      // nuevo-emisor gate so the client onboarding page can branch into the
      // homologación-simplificada phase set for "scratch". Best-effort: a
      // hiccup here just falls back to the default DGI path — never blocks
      // the wizard navigation.
      try {
        await updateTenant({ onboardingSituationChoice: emisorChoice });
      } catch {
        /* non-fatal — client is created; choice defaults to existing path */
      }
      toast.success(
        t("accountantNewClient.created", "Cliente agregado."),
      );
      // The new tenant is now active. Dismiss the owner-facing "Invitá a
      // tu contador" nudge for this tenant up-front — when the eventual
      // owner lands on the dashboard, the accountant is already attached
      // and that step never had to surface. Fire-and-forget so a network
      // hiccup here doesn't block the wizard navigation below.
      void dismissDashboardAlert(ONBOARDING_NUDGE_ACCOUNTANT_INVITE).catch(
        () => {},
      );
      // Skimmed accountant-focused setup (cert + auto-flow trigger), not
      // the full owner-targeted onboarding wizard.
      navigate(`/accountant/client/${tenantId}/onboarding`, {
        replace: true,
      });
    } catch (err: unknown) {
      const data =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data
          : undefined;
      const msg = data?.message as string | undefined;
      toast.error(
        translateAuthErrorMessage(msg, t) ??
          t(
            "accountantNewClient.createError",
            "No pudimos crear la empresa.",
          ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={step}
            total={2}
            label={
              step === 1
                ? t("accountantNewClient.stepLabel", "Cliente")
                : t("accountantNewClient.stepLabelEmisor", "Emisor")
            }
          />
          {step === 1 ? (
            <>
              <h1 className="fx-onb-h1">
                {t("accountantNewClient.titleLead", "Buscamos a tu")}{" "}
                <span className="ital">
                  {t("accountantNewClient.titleAccent", "cliente.")}
                </span>
              </h1>
              <p className="fx-onb-lead">
                {t(
                  "accountantNewClient.lead",
                  "Ingresá el RUT — 12 dígitos — y Fiscalo trae los datos directo desde DGI. Vas a quedar como propietario; podés transferir la propiedad al titular real más adelante.",
                )}
              </p>
            </>
          ) : (
            <>
              <h1 className="fx-onb-h1">
                {t("accountantNewClient.emisorTitleLead", "Tipo de")}{" "}
                <span className="ital">
                  {t("accountantNewClient.emisorTitleAccent", "emisor.")}
                </span>
              </h1>
              <p className="fx-onb-lead">
                {t(
                  "accountantNewClient.emisorLead",
                  "¿Tu cliente ya factura electrónicamente, o lo damos de alta desde cero? Esto define cómo lo configuramos en DGI.",
                )}
              </p>
            </>
          )}

          {step === 1 && (
          <div className="fx-onb-form">
            <div className="fx-onb-form-row with-btn">
              <div className="fx-onb-field" style={{ gridColumn: "span 2" }}>
                <label htmlFor="new-client-rut">
                  {t("accountantNewClient.rutLabel", "RUT del cliente")}
                </label>
                <input
                  id="new-client-rut"
                  type="text"
                  inputMode="numeric"
                  placeholder="000000000000"
                  value={rut}
                  onChange={(e) => {
                    setRut(e.target.value);
                    if (verified) setLegalName(null);
                  }}
                  maxLength={RUC_DIGITS + 4}
                  readOnly={verified}
                  disabled={submitting}
                />
              </div>
              <button
                type="button"
                className={cn("fx-onb-btn-validate", verified && "success")}
                onClick={handleValidateRut}
                disabled={validating || submitting || !rutValid || verified}
              >
                {validating ? (
                  <>
                    <Loader2 className="animate-spin" aria-hidden />
                    {t("accountantNewClient.validating", "Buscando…")}
                  </>
                ) : verified ? (
                  <>
                    <Check aria-hidden />
                    {t("accountantNewClient.verified", "Verificado")}
                  </>
                ) : (
                  <>
                    <Search aria-hidden />
                    {t("accountantNewClient.search", "Buscar")}
                  </>
                )}
              </button>
            </div>

            {verified ? (
              <>
                <div className="fx-onb-verified-block">
                  <div className="vb-head">
                    <span className="title">
                      {t("accountantNewClient.dataTitleLead", "Datos")}{" "}
                      <span className="ital">
                        {t("accountantNewClient.dataTitleAccent", "de DGI.")}
                      </span>
                    </span>
                    <span className="verified">
                      <span className="dot">
                        <Check aria-hidden />
                      </span>
                      {t("accountantNewClient.verified", "Verificado")}
                    </span>
                  </div>
                  <div className="vb-grid">
                    <div className="fx-onb-vb-item">
                      <div className="k">
                        {t(
                          "accountantNewClient.razonSocial",
                          "Razón social",
                        )}
                      </div>
                      <div className="v">{legalName}</div>
                    </div>
                    <div className="fx-onb-vb-item">
                      <div className="k">
                        {t("accountantNewClient.direccion", "Dirección")}
                      </div>
                      <div className="v">
                        {legalAddress ||
                          t("accountantNewClient.fallback", "—")}
                      </div>
                    </div>
                    <div className="fx-onb-vb-item">
                      <div className="k">
                        {t(
                          "accountantNewClient.departamento",
                          "Departamento",
                        )}
                      </div>
                      <div className="v">
                        {department ||
                          city ||
                          t("accountantNewClient.fallback", "—")}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fx-onb-form" style={{ marginTop: 8 }}>
                  <div className="fx-onb-field">
                    <label htmlFor="new-client-company-type">
                      {t(
                        "accountantNewClient.companyType",
                        "Tipo de empresa",
                      )}
                    </label>
                    <Select
                      value={companyType === "" ? undefined : companyType}
                      onValueChange={(v) =>
                        setCompanyType(v as CompanyTypeValue)
                      }
                      disabled={submitting}
                    >
                      <SelectTrigger id="new-client-company-type">
                        <SelectValue
                          placeholder={t(
                            "accountantNewClient.companyTypePlaceholder",
                            "Seleccioná un tipo",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sas">SAS</SelectItem>
                        <SelectItem value="srl">SRL</SelectItem>
                        <SelectItem value="sa">S.A.</SelectItem>
                        <SelectItem value="unipersonal">Unipersonal</SelectItem>
                        <SelectItem value="monotributo">Monotributo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="fx-onb-field">
                    <label>
                      {t(
                        "accountantNewClient.contactSection",
                        "Datos de contacto del cliente",
                      )}
                    </label>
                  </div>
                  <div className="fx-onb-form-row">
                    <div className="fx-onb-field">
                      <label htmlFor="new-client-email">
                        {t(
                          "accountantNewClient.contactEmail",
                          "Correo de contacto",
                        )}
                      </label>
                      <input
                        id="new-client-email"
                        type="email"
                        placeholder="cliente@empresa.com"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                    <div className="fx-onb-field">
                      <label htmlFor="new-client-phone">
                        {t("accountantNewClient.contactPhone", "Teléfono")}
                      </label>
                      <input
                        id="new-client-phone"
                        type="tel"
                        placeholder="099 000 000"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                  </div>

                </div>
              </>
            ) : null}
          </div>
          )}

          {step === 2 && (
            <div className="fx-onb-form">
              <div className="fx-onb-choice-grid stack">
                <OnboardingChoiceCard
                  icon={<Sparkles aria-hidden />}
                  name={t(
                    "accountantNewClient.emisorChoice.scratch",
                    "Nuevo emisor",
                  )}
                  desc={t(
                    "accountantNewClient.emisorChoice.scratchDesc",
                    "Todavía no factura electrónicamente. Lo damos de alta con la homologación simplificada de DGI.",
                  )}
                  selected={emisorChoice === "scratch"}
                  disabled={submitting}
                  onClick={() => setEmisorChoice("scratch")}
                />
                <OnboardingChoiceCard
                  icon={<FileCheck aria-hidden />}
                  name={t(
                    "accountantNewClient.emisorChoice.alreadyIssuer",
                    "Ya es emisor electrónico",
                  )}
                  desc={t(
                    "accountantNewClient.emisorChoice.alreadyIssuerDesc",
                    "Ya factura electrónicamente y tiene certificado. Configuramos Fiscalo como proveedor en DGI.",
                  )}
                  selected={emisorChoice === "already_issuer"}
                  disabled={submitting}
                  onClick={() => setEmisorChoice("already_issuer")}
                />
              </div>
            </div>
          )}

          <div className="fx-onb-actions">
            <div className="side">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-secondary"
                onClick={() =>
                  step === 1 ? navigate("/accountant") : setStep(1)
                }
                disabled={submitting}
              >
                <ArrowLeft aria-hidden />
                {t("accountantNewClient.back", "Volver")}
              </button>
            </div>
            <div className="side">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-primary"
                onClick={() => (step === 1 ? setStep(2) : void handleSubmit())}
                disabled={step === 1 ? !canContinue || submitting : submitting}
              >
                {submitting ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : (
                  <>
                    {t("accountantNewClient.continue", "Continuar")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t(
            "accountantNewClient.brandEyebrow",
            "Agregá clientes en segundos",
          )}
          quote={
            <>
              {t(
                "accountantNewClient.brandQuoteLead",
                "Tu cliente,",
              )}{" "}
              <span className="accent">
                {t(
                  "accountantNewClient.brandQuoteAccent",
                  "configurado desde DGI.",
                )}
              </span>
            </>
          }
        />
      }
    />
  );
}
